<!-- eslint-disable max-len -->
<template>
  <div class="bg-white">
    <v-container
      v-if="loaded"
      class="mxw-800 py-8"
      fluid
    >
      <div data-cy="alert">
        <div class="d-flex align-center mb-6">
          <h1
            class="fs-28 fw-600"
            tabindex="0"
          >
            {{ subsidyProgram.name }}
          </h1>
          <v-spacer />
          <v-btn
            :aria-label="$bct('Download application')"
            :href="link"
            class="focus-very-visible"
            color="secondary"
            target="_blank"
            variant="outlined"
          >
            <span class="fs-16">
              {{ $bct('Download') }}
            </span>
          </v-btn>
        </div>

        <v-alert
          v-if="ineligible"
          border="start"
          class="focus-very-visible w-100pc mt-6 fs-20"
          color="primary"
          tabindex="0"
          type="info"
        >
          <div>
            <div>
              {{
                $bct(
                  'You are not eligible for this program at this time. Please contact support if you believe this is incorrect.',
                )
              }}
            </div>
          </div>
        </v-alert>

        <v-alert
          v-if="eligible && readonly"
          border="start"
          class="w-100pc mb-8 fs-20"
          color="primary"
          tabindex="0"
          type="info"
        >
          <span
            v-if="subsidy.submitted_at"
            class="me-1"
            data-testid="application-submitted-at-readonly"
          >
            <LongDateTime
              :date="subsidy.submitted_at"
              prefix="Application submitted on"
            />.
          </span>
          <span>
            {{ $bct('At this time your application is locked and you cannot make edits.') }}
          </span>
        </v-alert>

        <v-alert
          v-if="!readonly"
          border="start"
          class="focus-very-visible w-100pc mb-8 fs-20"
          color="secondary"
          tabindex="0"
          type="info"
        >
          <span
            v-if="subsidy.submitted_at"
            class="me-1"
            data-testid="application-submitted-at-editable"
          >
            <LongDateTime
              v-if="subsidy.submitted_at"
              :date="subsidy.submitted_at"
              prefix="Application submitted on"
            />.
          </span>

          <span
            v-if="subsidyProgram.closes_at && !subsidyProgramClosed"
            class="me-1"
          >
            <LongDate
              v-if="subsidyProgram.closes_at"
              :date="subsidyProgram.closes_at"
              prefix="You can make edits until"
            />.
          </span>
        </v-alert>
      </div>

      <SubsidyEligibilityCard
        v-if="subsidy.meta?.funding_sources?.length"
        :elevation="2"
        :funding-sources="subsidy.meta.funding_sources"
        :name="child.name"
        :projected-eligibility="subsidy.projected_eligibility"
        class="focus-very-visible mb-6"
        tabindex="0"
        expanded
        explanation
        outlined
      />

      <FormQuestion
        :processing="processing"
        :subtitle="subsidyProgram.home_address_subtitle"
        :title="subsidyProgram.home_address_title || 'What is your family\'s primary home address.'"
        class="mb-4"
        dense
        expanded
        hide-actions
        paddingless
      >
        <v-row>
          <LabeledTextfield
            v-model="group.home_address"
            @change="registerChange"
            :hard-lock="readonly"
            :readonly="readonly"
            :schema-id="group.schema_id"
            data-cy="home_address"
            field="home_address"
            message="Address"
            dense
            mandatory
          />
          <LabeledTextfield
            v-model="group.home_city"
            @change="registerChange"
            :hard-lock="readonly"
            :readonly="readonly"
            :schema-id="group.schema_id"
            field="home_city"
            md="4"
            message="City"
            dense
            mandatory
          />
          <LabeledSimpleSelect
            v-model="group.home_state"
            @change="registerChange"
            :hard-lock="readonly"
            :items="$a.assets.states"
            :readonly="readonly"
            :schema-id="group.schema_id"
            field="home_state"
            md="4"
            message="State"
            placeholder="Select one"
            dense
            mandatory
          />
          <LabeledTextfield
            v-model="group.home_zip"
            @change="registerChange"
            :hard-lock="readonly"
            :readonly="readonly"
            :schema-id="group.schema_id"
            field="home_zip"
            md="4"
            message="Zip"
            dense
            mandatory
          />
        </v-row>
        <v-row
          v-if="subsidyProgram.enable_homeless_address_option"
          dense
        >
          <v-checkbox
            v-model="group.experiencing_homelessness"
            @update:model-value="registerChange"
            :label="
              $bct(subsidyProgram.homeless_attestation || 'My family is experiencing homelessness.')
            "
            :readonly="readonly"
          />
        </v-row>
      </FormQuestion>

      <v-divider class="mb-4" />

      <FormQuestion
        :key="child.id"
        :processing="processing"
        :title="child.name"
        class="mb-4"
        break-after
        dense
        hide-actions
        paddingless
      >
        <Child
          @change="registerChange"
          :hard-lock="readonly"
          :initial-child="child"
          :readonly="readonly"
          dense
          prevent-update
          remove-disabled
        />
      </FormQuestion>

      <v-divider class="mb-4" />

      <ThirdPartyQuestions
        v-if="subsidyProgram.enable_third_party_applications"
        key="third-party-questions"
        :model-value="subsidy"
        :processing="processing"
        :readonly="readonly"
        :reversible="false"
      />

      <v-divider class="my-8" />

      <div data-testid="group-eligibility-questions">
        <QuestionSet
          v-model="group"
          @change="registerChange"
          @change:attachments="loadAttachments()"
          :attachment-group-id="group.id"
          :attachment-owner-id="subsidyProgram.id"
          :attachment-owner-type="'SubsidyProgram'"
          :attachment-upload-disabled="attachmentUploadDisabled"
          :attachments="attachments"
          :processing="processing"
          :questions="validGroupEligibilityQuestions"
          :readonly="readonly"
          :schema="groupSchema.definition"
          dense
          divided
          expanded
          hide-actions
          paddingless
        />
      </div>

      <div data-testid="child-eligibility-questions">
        <QuestionSet
          @change="registerChange"
          @change:attachments="loadAttachments()"
          @input="child = $event"
          :key="child.id + '-eligibility'"
          :attachment-group-id="group.id"
          :attachment-owner-id="subsidyProgram.id"
          :attachment-owner-type="'SubsidyProgram'"
          :attachment-tags-supplements="[subsidy.id]"
          :attachment-upload-disabled="attachmentUploadDisabled"
          :attachments="attachments"
          :header="$bct('Child') + ' - ' + child.first_name"
          :model-value="child"
          :processing="processing"
          :questions="applicantEligibilityQuestions[child.id].filter((question) => question.valid)"
          :readonly="readonly"
          :schema="childSchema?.definition"
          dense
          divided
          expanded
          hide-actions
          paddingless
        />
      </div>

      <template v-if="!ineligible">
        <QuestionSet
          v-model="group"
          @change="registerChange"
          @change:attachments="loadAttachments()"
          :attachment-group-id="group.id"
          :attachment-owner-id="subsidyProgram.id"
          :attachment-owner-type="'SubsidyProgram'"
          :attachment-upload-disabled="attachmentUploadDisabled"
          :attachments="attachments"
          :processing="processing"
          :questions="validGroupQuestions"
          :readonly="readonly"
          :schema="groupSchema.definition"
          dense
          divided
          expanded
          hide-actions
          paddingless
        />

        <QuestionSet
          @change="registerChange"
          @change:attachments="loadAttachments()"
          @input="child = $event"
          :key="child.id + '-other'"
          :attachment-group-id="group.id"
          :attachment-owner-id="subsidyProgram.id"
          :attachment-owner-type="'SubsidyProgram'"
          :attachment-tags-supplements="[subsidy.id]"
          :attachment-upload-disabled="attachmentUploadDisabled"
          :attachments="attachments"
          :header="$bct('Child') + ' - ' + child.first_name"
          :model-value="child"
          :processing="processing"
          :questions="applicantQuestions[child.id].filter((question) => question.valid)"
          :readonly="readonly"
          :schema="childSchema?.definition"
          dense
          divided
          expanded
          hide-actions
          paddingless
        />

        <template v-if="preferencesSectionEnabled">
          <template v-if="subsidyProgram.enable_advanced_selector">
            <template v-if="subsidy.locked">
              <template v-if="subsidyProgram.allow_program_preference">
                <ProgramPreferenceReview
                  :child-name="child.first_name"
                  :current-provider-id="subsidy.current_provider_id"
                  :program-ids="subsidy.program_ids"
                  :sibling-provider-ids="subsidy.sibling_provider_ids"
                  :staff-provider-ids="subsidy.staff_provider_ids"
                  :subsidy-program="subsidyProgram"
                />
              </template>
              <template v-else>
                <ProviderPreferenceReview
                  :child-name="child.first_name"
                  :current-provider-id="subsidy.current_provider_id"
                  :provider-ids="subsidy.provider_ids"
                  :subsidy-program="subsidyProgram"
                />
              </template>
            </template>
            <template v-else>
              <template v-if="subsidyProgram.allow_program_preference">
                <ProgramPreferenceEditor
                  @change="registerChange"
                  @reorder-programs="(from, to) => handleReorderSelectedPrograms(from, to)"
                  @selected-current-provider-id="handleSelectedCurrentProvider"
                  @selected-program-ids="(obj) => handleSelectedPrograms(obj)"
                  @selected-sibling-ids="handleSelectedSiblings"
                  @selected-staff-ids="handleSelectedStaff"
                  :child-data="child"
                  :current-provider="subsidy.current_provider_id"
                  :default-query="homeAddress"
                  :processing="processing"
                  :sibling-providers="subsidy.sibling_provider_ids"
                  :staff-providers="subsidy.staff_provider_ids"
                  :subsidy="subsidy"
                  :subsidy-program="subsidyProgram"
                  hide-actions
                  show-selected
                />
              </template>
              <template v-else>
                <ProviderPreferenceEditor
                  v-model="subsidy"
                  @change="registerChange"
                  :child-data="child"
                  :default-query="homeAddress"
                  :processing="processing"
                  :subsidy-program="subsidyProgram"
                  hide-actions
                />
              </template>
            </template>
          </template>
          <template v-else>
            <template v-if="subsidyProgram.allow_preference">
              <FormQuestion
                :header="$bct('Child') + ' - ' + child.first_name"
                :subtitle="
                  subsidyProgram.preference_subtitle ||
                  (subsidyProgram.allow_preference_order
                    ? 'Selecting a provider indicates that you’re interested in receiving care from this provider. Rank your favorites by reordering the selected providers from top-bottom. Top being your top choice. We’ll do our best to match you with a provider of your choice!'
                    : 'Selecting a provider indicates that you’re interested in receiving care from this provider.')
                "
                :title="
                  subsidyProgram.preference_title ||
                  (subsidyProgram.allow_preference_order
                    ? 'Select and rank the providers you’d like to include in the application for this child'
                    : 'Select the providers you’d like to include in the application for this child')
                "
                dense
                hide-actions
                paddingless
              >
                <FavoritesSelector
                  @update-provider-ids="handleProviderIdsChange"
                  :child-data="child"
                  :ordered="subsidyProgram.allow_preference_order"
                  :provider-ids="subsidy.provider_ids"
                  :readonly="readonly"
                  :subsidy="subsidy"
                  :subsidy-program="subsidyProgram"
                  class="mt-4 mb-4"
                  initial-mode="selected"
                />
              </FormQuestion>
            </template>

            <template v-if="subsidyProgram.allow_enrolled">
              <FormQuestion
                :header="$bct('Child') + ' - ' + child.first_name"
                :subtitle="subsidyProgram.preference_subtitle"
                :title="
                  subsidyProgram.preference_title ||
                  'If your child is already enrolled in care, add the provider below.'
                "
                dense
                hide-actions
                paddingless
              >
                <ProviderSelector
                  v-model:subsidy="subsidy"
                  @change="registerChange"
                  :child="child"
                />
              </FormQuestion>
            </template>
          </template>
        </template>

        <div
          v-for="question in groupVerificationQuestions"
          :key="question.id"
        >
          <v-divider class="my-4" />

          <FormQuestion
            :subtitle="question.verification_subtitle"
            :title="question.verification_title"
            dense
            hide-actions
            paddingless
          >
            <AttachmentUploader
              v-if="!attachmentUploadDisabled"
              @uploaded="attachments.push($event)"
              :ref="['uploader', question.id].join('')"
              :owner="questionOwner(question)"
              class="mb-4"
            />
            <AttachmentList
              @delete="loadAttachments"
              :attachments="attachments.filter((attachment) => attachment.tag == question.id)"
              :hide-remove="readonly"
              :processing="processing"
              empty-label="No documents provided."
              dense
            />
          </FormQuestion>
        </div>

        <div
          v-for="question in childVerificationQuestions"
          :key="question.id + child.id"
        >
          <v-divider class="my-4" />

          <FormQuestion
            :header="'Child - ' + child.first_name"
            :subtitle="question.verification_subtitle"
            :title="question.verification_title"
            dense
            hide-actions
            paddingless
          >
            <AttachmentUploader
              v-if="!attachmentUploadDisabled"
              @uploaded="attachments.push($event)"
              :ref="['uploader', question.id, child.id].join('')"
              :owner="childVerificationQuestionUploaderOwner(question)"
              class="mb-4"
            />
            <AttachmentList
              @delete="loadAttachments"
              :attachments="
                attachments.filter((attachment) => attachment.tag == child.id + question.id)
              "
              :hide-remove="readonly"
              :processing="processing"
              empty-label="No documents provided."
              dense
            />
          </FormQuestion>
        </div>

        <template v-if="subsidyProgram.verify_child">
          <div>
            <v-divider class="my-4" />
            <FormQuestion
              :header="$bct('Child') + ' - ' + child.first_name"
              :subtitle="subsidyProgram.verify_child_subtitle"
              :title="subsidyProgram.verify_child_title"
              dense
              hide-actions
              paddingless
            >
              <AttachmentUploader
                v-if="!attachmentUploadDisabled"
                @uploaded="attachments.push($event)"
                :owner="{
                  type: 'SubsidyProgram',
                  id: subsidyProgram.id,
                  tag: 'child-documents-' + child.id,
                  tags: ['child-documents', child.id, subsidy.id],
                }"
                class="mb-4"
              />
              <AttachmentList
                @delete="loadAttachments"
                :attachments="
                  attachments.filter(
                    (attachment) => attachment.tag == 'child-documents-' + child.id,
                  )
                "
                :hide-remove="readonly"
                :processing="processing"
                empty-label="No documents provided."
                dense
              />
            </FormQuestion>
          </div>
        </template>

        <template v-if="subsidyProgram.verify_home_address">
          <v-divider class="my-4" />

          <FormQuestion
            :subtitle="
              subsidyProgram.verify_home_address_subtitle ||
              'Ex. A copy of current lease, proof of homeownership, or utility bill (with service or premise address listed) such as your bill for gas, electric, water, or cable.'
            "
            :title="
              subsidyProgram.verify_home_address_title ||
              'Upload a document to verify your current address'
            "
            dense
            hide-actions
            paddingless
          >
            <AttachmentUploader
              v-if="!attachmentUploadDisabled"
              @uploaded="attachments.push($event)"
              ref="uploader_residency"
              :owner="proofOfResidencyQuestionUploaderOwner"
              class="mb-4"
            />
            <AttachmentList
              @delete="loadAttachments"
              :attachments="
                attachments.filter((attachment) => attachment.tag == 'proof-of-residency')
              "
              :hide-remove="readonly"
              :processing="processing"
              empty-label="No documents provided."
              dense
            />
          </FormQuestion>
        </template>
        <v-divider class="my-8" />

        <div v-if="!readonly">
          <div>
            <FormQuestion
              @finish="saveAndSubmit"
              :finish-disabled="!confirmed"
              :forwardable="false"
              :processing="processing"
              :reversible="false"
              :title="subsidyProgram.confirm_title || 'Confirm your application information'"
              class="mb-4"
              dense
              finishable
            >
              <UserAttestation
                v-if="subsidy.third_party_application && subsidyProgram.enable_third_party_esign"
                @change="confirmed = $event"
                ref="attestationRef"
                :attestation-label="
                  subsidyProgram.third_party_attestation_label[$store.state.profile.default_locale]
                "
                :attestation-text="subsidyProgram.third_party_attestation_text"
                :enable-esign="true"
              />

              <UserAttestation
                v-else
                @change="confirmed = $event"
                ref="attestationRef"
                :attestation-label="subsidyProgram.attestation_label"
                :attestation-text="subsidyProgram.attestation_text"
                :enable-esign="subsidyProgram.enable_esign"
              />
            </FormQuestion>
          </div>
        </div>
      </template>
    </v-container>

    <ConfirmDialog ref="confirmDialog" />
  </div>
</template>

<script setup>
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { scrollTo } from 'vuetify/lib/composables/goto';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import AttachmentList from '@/shared/components/attachments/AttachmentList.vue';
import AttachmentUploader from '@/shared/components/attachments/AttachmentUploader.vue';
import Child from '@/parent/components/children/Child.vue';
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import FavoritesSelector from '@/parent/components/favorites/FavoritesSelector.vue';
import FormQuestion from '@/shared/components/form/FormQuestion.vue';
import LabeledSimpleSelect from '@/shared/components/form/LabeledSimpleSelect.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import LongDate from '@/shared/components/LongDate.vue';
import LongDateTime from '@/shared/components/LongDateTime.vue';
import ProgramPreferenceEditor from '@/parent/components/ProgramPreferenceEditor.vue';
import ProgramPreferenceReview from '@/parent/components/subsidy/ProgramPreferenceReview.vue';
import ProviderPreferenceEditor from '@/parent/components/ProviderPreferenceEditor.vue';
import ProviderPreferenceReview from '@/parent/components/subsidy/ProviderPreferenceReview.vue';
import ProviderSelector from '@/parent/components/ProviderSelector.vue';
import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import { SUBSIDY_EVENTS } from '@/parent/services/constants';
import SubsidyApplicationData from '@/shared/services/subsidy/application-data.js';
import SubsidyEligibilityCard from '@/shared/components/subsidy/SubsidyEligibilityCard.vue';
import ThirdPartyQuestions from '@/parent/components/family-subsidies/ThirdPartyQuestions.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';
import UserAttestation from '@/shared/components/UserAttestation.vue';
import useSubsidy from '@/shared/composables/useSubsidy';

const LEAVE_WARNING =
  'You have unsaved changes and have not submitted your application. ' +
  'Are you sure you want to leave your application?';

const props = defineProps({
  value: {
    type: Object,
    default: null,
  },
  childApplicationData: {
    type: Object,
    default: null,
  },
});

const { api: Api } = useApi();
const eventBus = useEventBus();
const { locale } = useI18n();
const store = useStore();
const $bct = inject('translator');

const attachments = ref([]);
const attestation = ref(null);
const attestationRef = ref(null);
const changed = ref(false);
const child = ref(null);
const children = ref([]);
const confirmDialog = ref(null);
const confirmed = ref(false);
const group = ref(null);
const providers = ref([]);
const processing = ref(false);
const route = useRoute();
const subsidy = ref(null);
const subsidies = ref([]);
const subsidyProgram = ref(null);
const subsidyProgramId = ref(null);
const validateCompleted = ref(false);

const {
  applicantEligibilityQuestions,
  applicantQuestions,
  childVerificationQuestions,
  childSchema,
  groupVerificationQuestions,
  groupSchema,
  loadSchemas,
  loadQuestions,
  validate,
  validGroupQuestions,
  validGroupEligibilityQuestions,
} = useSubsidy({ subsidyProgram, children, subsidies, group });

onBeforeRouteLeave(async (_to, _from, next) => {
  // Skip confirmation if we're auto-logging out due to session expiration
  if (window.isAutoLoggingOut) {
    window.onbeforeunload = null;
    return next();
  }

  if (changed.value) {
    const confirm = await confirmDialog.value.confirmWithText(LEAVE_WARNING);
    if (!confirm) return next(false);
  }

  window.onbeforeunload = null;
  next();
});

const attachmentUploadDisabled = computed(
  () => subsidyProgram.value.lock_parent_document_uploads && subsidy.value.locked,
);

const proofOfResidencyQuestionUploaderOwner = computed(() => {
  return {
    type: 'SubsidyProgram',
    id: subsidyProgram.value.id,
    tag: 'proof-of-residency',
    tags: ['proof-of-residency', subsidy.value.id],
  };
});

const eligible = computed(() => {
  return subsidy.value.projected_eligibility === true;
});

function handleProviderIdsChange(newProviderIds) {
  subsidy.value.provider_ids = newProviderIds;
  registerChange();
}

const ineligible = computed(() => {
  return subsidy.value.projected_eligibility === false;
});

const preferencesSectionEnabled = computed(() => {
  if (
    subsidyProgram.value.enable_iep_direct_placement &&
    child.value.individualized_education_plan
  ) {
    return false;
  }

  return (
    subsidyProgram.value.allow_preference ||
    subsidyProgram.value.allow_program_preference ||
    subsidyProgram.value.allow_enrolled
  );
});

const homeAddress = computed(() => {
  if (group.value) {
    return [
      group.value.home_address,
      group.value.home_city,
      group.value.home_state,
      group.value.home_zip,
    ].join(', ');
  }
  return '';
});

const link = computed(() => {
  return Api.parent.subsidy.downloadUrl(
    store.state.profile.group_id,
    route.params.subsidyId,
    store.state.profile.default_locale,
    'Application',
  );
});

const loaded = computed(() => {
  const childSchemaReady = subsidyProgram.value?.child_schema_id ? !!childSchema.value : true;
  return !!(
    child.value &&
    group.value &&
    subsidy.value &&
    subsidyProgram.value &&
    groupSchema.value &&
    childSchemaReady &&
    validateCompleted.value
  );
});

const locked = computed(() => {
  return !!subsidy.value.locked;
});

const readonly = computed(() => {
  return ineligible.value || locked.value || subsidyProgramClosed.value;
});

const subsidyProgramClosed = computed(() => {
  return subsidyProgram.value?.closes_at && new Date() >= new Date(subsidyProgram.value.closes_at);
});

onMounted(async () => {
  await load();
});

function questionOwner(someQuestion) {
  return {
    type: 'SubsidyProgram',
    id: subsidyProgram.value.id,
    tag: someQuestion.id,
    tags: [subsidy.value.id, someQuestion.id],
  };
}

function childVerificationQuestionUploaderOwner(someQuestion) {
  return {
    type: 'SubsidyProgram',
    id: subsidyProgram.value.id,
    tag: child.value.id + someQuestion.id,
    tags: [child.value.id, subsidy.value.id, someQuestion.id],
  };
}

function handleReorderSelectedPrograms(fromIndex, toIndex) {
  const reorderedProgramId = subsidy.value.program_ids[fromIndex];

  subsidy.value.program_ids.splice(fromIndex, 1);
  subsidy.value.program_ids.splice(toIndex, 0, reorderedProgramId);
  registerChange();
}

function handleSelectedPrograms({ type, id }) {
  switch (type) {
    case SUBSIDY_EVENTS.ADD: {
      return subsidy.value.program_ids.push(id);
    }
    case SUBSIDY_EVENTS.REMOVE:
      return subsidy.value.program_ids.splice(
        subsidy.value.program_ids.findIndex((programId) => programId === id),
        1,
      );
    default:
      return null;
  }
}

function handleSelectedCurrentProvider(providerId) {
  subsidy.value.current_provider_id = providerId;
  registerChange();
}

function handleSelectedSiblings(ids) {
  subsidy.value.sibling_provider_ids = ids;
  registerChange();
}

function handleSelectedStaff(ids) {
  subsidy.value.staff_provider_ids = ids;
  registerChange();
}

async function load() {
  if (!props.value) {
    const response = await Api.parent.subsidy.get(
      store.state.profile?.group_id,
      route.params.subsidyId,
    );
    subsidy.value = response.data;
  } else {
    subsidy.value = props.value;
  }
  subsidies.value = [subsidy.value];
  subsidyProgramId.value = subsidy.value.subsidy_program_id;

  await loadChild();
  await loadGroup();

  await loadSubsidyProgram();
  if (subsidyProgram.value?.name) {
    document.title = `Review application - ${subsidyProgram.value.name}`;
  }
  await loadSchemas();
  await loadAttachments();
  await loadQuestions();
  await validate(() => {
    validateCompleted.value = true;
  });
  setAttestation();

  providers.value = subsidy.value.provider_ids; // TODO: Consider deprecating this
}

async function loadChild() {
  // SubsidyReview route has no prop; ManageChildSubsidy passes childApplicationData to avoid a duplicate GET.
  if (props.childApplicationData) {
    child.value = props.childApplicationData;
    children.value = [child.value];
    return;
  }

  const resp = await SubsidyApplicationData.getChildData(subsidy.value.id);
  if (resp?.data) {
    child.value = resp.data;
    child.value.included = true;
    children.value = [child.value];
  }
}

async function loadGroup() {
  const resp = await SubsidyApplicationData.getGroupData(subsidy.value.id);
  if (resp?.data) {
    group.value = resp.data;
  }
}

async function loadAttachments() {
  processing.value = true;
  const params = {
    owner_type: 'SubsidyProgram',
    owner_id: subsidy.value.subsidy_program_id,
  };
  const resp = await Api.member.attachment.index(params);
  if (resp?.status === 200) {
    attachments.value = resp.data;
  }
  processing.value = false;
}

async function loadSubsidyProgram() {
  const resp = await Api.shared.subsidyProgram.get(subsidyProgramId.value);
  if (resp?.data) {
    subsidyProgram.value = resp.data;
  }
}

function registerChange() {
  window.onbeforeunload = () => {
    // Return a truthy value to trigger browser's native "Leave site?" dialog
    // This only works for actual page navigation/refresh, not Vue Router navigation
    if (changed.value && !window.isAutoLoggingOut) {
      return 'You have unsaved information.';
    }
  };

  changed.value = true;
  setTimeout(() => {
    validate();
  }, 50);
}

async function saveAndSubmit() {
  if (!subsidy.value.submitted_at || !subsidy.value.locked) {
    subsidy.value.submitted = true;
  }

  if (subsidyProgram.value.enable_esign) {
    subsidy.value.revision_author_signed_name = attestationRef.value.esignName;
    subsidy.value.revision_author_signature_image = attestationRef.value.getEsignSignature();
  }

  subsidy.value.revision_description = attestationRef.value.getAttestationText();

  processing.value = true;

  await Api.member.childApplicationData.update(subsidy.value.id, child.value);
  await Api.member.groupApplicationData.update(subsidy.value.id, group.value);
  const resp = await Api.parent.subsidy.update(
    store.state.profile?.group_id,
    route.params.subsidyId,
    subsidy.value,
  );
  scrollTo(0);
  eventBus.chime('Updated');
  processing.value = false;
  subsidy.value = resp.data;
  subsidies.value = [subsidy.value];
  changed.value = false;
}

function setAttestation() {
  if (!subsidyProgram.value) return;
  const currentLocale = locale.value.split('-')[0] || 'en';
  const attestations = subsidyProgram.value.enable_third_party_esign
    ? subsidyProgram.value.third_party_attestation_text
    : subsidyProgram.value.attestation_text;
  if (!attestations) return;
  if (attestations[currentLocale]) {
    attestation.value = attestations[currentLocale];
  } else if (attestations.en) {
    attestation.value = attestations.en;
  } else {
    attestation.value = null; // or some default value
  }
}
</script>
